import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';
import { useGlobalConfig } from 'context/GlobalContext';
import { DataScenariosApi, CreateDataScenarioRequest, getTemplateNameForApi, getFuelTypeFromTemplateKey } from 'apis/dataScenariosAPI';
import { CMSPilotApis } from 'apis/cmsPilotAPI';
import { toast } from 'utils/toast';
import { useAvailableFuelTypes, getDefaultFuelType } from 'hooks/useProjectFuelTypes';
import { mapMeasurementTypeToFuelType } from './dataScenariosUtils';
import { FullScreenJsonEditorModal } from './FullScreenJsonEditorModal';

// Locale type from API
interface LocaleOption {
  code: string;
  name: string;
}

const LineArrowIcon = (props: any) => (
  <Box
    {...props}
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...props.sx
    }}
  >
    <Box
      component="svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.6"
      />
    </Box>
  </Box>
);


interface Payload {
  id: string;
  endpoint: string;
  response: string;
  expanded: boolean;
  isValidJson: boolean;
}

interface AddDataScenariosProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  pilotId: string;
  templateKey: string;       // e.g., "EMAIL.SF.MONTHLY_SUMMARY"
  templateDisplayName: string; // e.g., "Monthly Summary"
  elementKey?: string;       // e.g., "HEADER_V4" or undefined for full template
  elementDisplayName?: string; // e.g., "Header" or "Full Template"
  hasExistingDefault?: boolean;
}

export const AddDataScenarios: React.FC<AddDataScenariosProps> = ({
  open,
  onClose,
  onSave,
  pilotId,
  templateKey,
  templateDisplayName,
  elementKey,
  elementDisplayName = 'Full Template',
  hasExistingDefault = false
}) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  
  // Form state
  const [scenarioId, setScenarioId] = useState('');
  const [label, setLabel] = useState('');
  const [configLevel] = useState('Project');
  const [templateType, setTemplateType] = useState('Email');
  const [locale, setLocale] = useState('English (US)');
  const [fuelType, setFuelType] = useState('ELECTRIC');
  const [isDefaultScenario, setIsDefaultScenario] = useState(false);
  const [payloads, setPayloads] = useState<Payload[]>([
    {
      id: '1',
      endpoint: '',
      response: '{}',
      expanded: true,
      isValidJson: true
    }
  ]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [fullScreenPayloadId, setFullScreenPayloadId] = useState<string | null>(null);

  const [locales, setLocales] = useState<LocaleOption[]>([]);

  const { availableFuelTypes, projectAttributes } = useAvailableFuelTypes(rc, pilotId, open);

  const getTemplateTypeFromKey = (key: string): string => {
    if (!key) return 'Email';
    if (key.startsWith('HER.')) return 'HER';
    if (key.startsWith('WEB_') || key.startsWith('DASHBOARD.')) return 'Web';
    if (key.startsWith('SMS.')) return 'SMS';
    return 'Email';
  };

  const filteredFuelTypes = React.useMemo(() => {
    if (!templateKey) return availableFuelTypes;

    const templateFuelType = getFuelTypeFromTemplateKey(templateKey);
    
    if (templateFuelType) {
      const mappedFuelType = mapMeasurementTypeToFuelType(templateFuelType);
      if (mappedFuelType) return [mappedFuelType];
    }
    
    return availableFuelTypes;
  }, [templateKey, availableFuelTypes]);

  useEffect(() => {
    if (!open || !templateKey) return;

    const detectedTemplateType = getTemplateTypeFromKey(templateKey);
    setTemplateType(detectedTemplateType);

    const defaultFuelType = getDefaultFuelType(templateKey, projectAttributes, filteredFuelTypes);
    setFuelType(defaultFuelType);
  }, [open, templateKey, projectAttributes, filteredFuelTypes]);

  useEffect(() => {
    const fetchLocales = async () => {
      try {
        const response = await CMSPilotApis.getSupportedLocales(rc);
        if (!response.error && response.data) {
          setLocales(response.data);
        }
      } catch (error) {
        console.error('[AddDataScenarios] Failed to fetch locales:', error);
      }
    };
    
    if (open) {
      fetchLocales();
    }
  }, [rc, open]);

  const getLocaleCode = (name: string): string => {
    const found = locales.find(l => l.name === name);
    if (found) return found.code;
    
    const localeMap: Record<string, string> = {
      'English (US)': 'en_US',
      'English (UK)': 'en_GB',
      'Spanish': 'es_ES',
      'Spanish (US)': 'es_US',
      'French': 'fr_FR',
      'French (Canada)': 'fr_CA',
      'German': 'de_DE',
      'Portuguese': 'pt_PT',
      'Portuguese (Brazil)': 'pt_BR',
      'Chinese (Simplified)': 'zh_CN',
      'Chinese (Traditional)': 'zh_TW',
      'Japanese': 'ja_JP',
      'Korean': 'ko_KR',
    };
    
    return localeMap[name] || name;
  };

  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to format JSON
  const formatJson = (str: string): string => {
    try {
      const parsed = JSON.parse(str);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return str;
    }
  };

  const handleAddPayload = () => {
    const newPayload: Payload = {
      id: Date.now().toString(),
      endpoint: '',
      response: '{}',
      expanded: true,
      isValidJson: true
    };
    setPayloads([...payloads, newPayload]);
  };

  const handleDeletePayload = (id: string) => {
    if (payloads.length > 1) {
      setPayloads(payloads.filter(p => p.id !== id));
    } else {
      toast.warning('At least one payload is required');
    }
  };

  const handleTogglePayload = (id: string) => {
    setPayloads(payloads.map(p => 
      p.id === id ? { ...p, expanded: !p.expanded } : p
    ));
  };

  const handleUpdatePayload = (id: string, field: 'endpoint' | 'response', value: string) => {
    setPayloads(payloads.map(p => 
      p.id === id ? { 
        ...p, 
        [field]: value,
        isValidJson: field === 'response' ? isValidJson(value) : p.isValidJson
      } : p
    ));
  };

  const handleFormatJson = (id: string) => {
    setPayloads(payloads.map(p => {
      if (p.id === id && isValidJson(p.response)) {
        return { ...p, response: formatJson(p.response) };
      }
      return p;
    }));
  };

  // Validate form before submission
  const validateForm = (): string | null => {
    if (!scenarioId.trim()) {
      return 'Scenario ID is required';
    }
    if (!label.trim()) {
      return 'Label is required';
    }
    if (!pilotId) {
      return 'Please select a project first';
    }
    if (!templateKey) {
      return 'Template is required';
    }
    
    // Validate payloads
    for (let i = 0; i < payloads.length; i++) {
      const payload = payloads[i];
      if (!payload.endpoint.trim()) {
        return `Payload ${i + 1}: Endpoint is required`;
      }
      if (!payload.isValidJson) {
        return `Payload ${i + 1}: Response must be valid JSON`;
      }
    }
    
    return null;
  };

  const handleSave = async () => {
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Determine final fuel type based on template key
    const templateFuelType = getFuelTypeFromTemplateKey(templateKey);

    // For templates with fuel type in the key, use it (mapped if needed)
    // For templates without fuel type (e.g., USER_WELCOME), use the current fuelType state
    let finalFuelType: string;
    if (templateFuelType) {
      const mappedTemplateFuelType = mapMeasurementTypeToFuelType(templateFuelType);
      finalFuelType = mappedTemplateFuelType || templateFuelType;

      // Update state if it doesn't match
      if (finalFuelType !== fuelType) {
        setFuelType(finalFuelType);
      }
    } else {
      // No fuel type in template key - use current state (user can select from dropdown)
      finalFuelType = fuelType;
    }

    setIsLoading(true);

    try {
      const templateNameForApi = getTemplateNameForApi(templateKey);

      // Determine elementNames based on templateType
      let elementNames: string[];
      if (templateType === 'Web') {
        elementNames = [templateNameForApi];
      } else {
        // Use elementKey if provided, otherwise default to FULL_TEMPLATE
        elementNames = elementKey ? [elementKey] : ['FULL_TEMPLATE'];
      }

      // Transform payloads to API format
      const transformedPayloads = payloads.map(p => ({
        templateTypeName: p.endpoint.trim(),
        payload: JSON.parse(p.response)
      }));
      
      const requestData: CreateDataScenarioRequest = {
        pilotId: pilotId,
        scenarioId: scenarioId.trim(),
        scenarioDisplayName: label.trim(),
        productType: templateType,
        scenarioLocale: getLocaleCode(locale) || 'en_US',
        fuelType: finalFuelType,
        templateName: templateNameForApi,
        elementNames: elementNames,
        defaultScenario: isDefaultScenario,
        userClusters: ['DEFAULT'],
        scenarioDocumentation: `Adding the Data scenarios ${label.trim()} - ${scenarioId.trim()}`,
        payloads: transformedPayloads
      };

      console.log('[AddDataScenarios] Creating scenario with data:', requestData);

      // Call the API
      const response = await DataScenariosApi.createDataScenarioWithPayloads(rc, requestData);

      if (response.success) {
        toast.success(`Scenario "${label}" created successfully`);
        onSave?.();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to create scenario');
      }
    } catch (error: any) {
      console.error('[AddDataScenarios] Error creating scenario:', error);
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while saving
    
    // Reset form
    setScenarioId('');
    setLabel('');
    setLocale('English (US)');
    setFuelType('ELECTRIC');
    setIsDefaultScenario(false);
    setPayloads([
      {
        id: '1',
        endpoint: '',
        response: '{}',
        expanded: true,
        isValidJson: true
      }
    ]);
    onClose();
  };

  return (
  <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Add New Project Scenario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define the name and JSON data for this scenario's API mocks.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Scenario ID and Label Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Scenario ID <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g., my-unique-id"
                value={scenarioId}
                onChange={(e) => setScenarioId(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Label <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g., High Usage, Logged In"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            </Box>
          </Box>

          {/* Config Level, Template Type, Locale Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Config Level
              </Typography>
              <TextField
                value={configLevel}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading || !templateKey}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  {/* Only show the template type that matches the selected template from sidebar */}
                  {templateType === 'Email' && <MenuItem value="Email">Email</MenuItem>}
                  {templateType === 'SMS' && <MenuItem value="SMS">SMS</MenuItem>}
                  {templateType === 'HER' && <MenuItem value="HER">HER</MenuItem>}
                  {templateType === 'Web' && <MenuItem value="Web">Web</MenuItem>}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Locale
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  {locales.length > 0 ? (
                    locales.map((loc) => (
                      <MenuItem key={loc.code} value={loc.name}>
                        {loc.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={locale}>{locale}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Template Name, Element Name, Fuel Type Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Name
              </Typography>
              <TextField
                value={templateDisplayName}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Element Name
              </Typography>
              <TextField
                value={elementDisplayName}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Fuel Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  {filteredFuelTypes.map((ft) => (
                    <MenuItem key={ft} value={ft}>
                      {ft}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Default Scenario Toggle */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDefaultScenario}
                  onChange={(e) => setIsDefaultScenario(e.target.checked)}
                  disabled={isLoading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Set as Default Scenario
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This scenario will be used as the default when no other scenario is selected
                  </Typography>
                </Box>
              }
              sx={{ ml: 0 }}
            />
            {/* Warning message - only show when replacing an existing default */}
            {isDefaultScenario && hasExistingDefault && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main', mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> Setting this as default will replace the existing default scenario for this template/element combination.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Scenario Payloads Section */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Scenario Payloads
            </Typography>

            {payloads.map((payload, index) => (
              <Accordion
                key={payload.id}
                expanded={payload.expanded}
                onChange={() => handleTogglePayload(payload.id)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '&.Mui-expanded': { margin: '0 0 8px 0' }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    payload.expanded ? (
                      <ExpandLessIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    )
                  }
                  sx={{
                    minHeight: 36,
                    '&.Mui-expanded': { minHeight: 36 },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 0.5
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Payload {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePayload(payload.id);
                      }}
                      disabled={isLoading}
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.1)
                        }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Endpoint <span style={{ color: theme.palette.error.main }}>*</span>
                      </Typography>
                      <TextField
                        value={payload.endpoint}
                        onChange={(e) => handleUpdatePayload(payload.id, 'endpoint', e.target.value)}
                        placeholder="e.g., bill-comparison, itemization-widget-data"
                        fullWidth
                        size="small"
                        disabled={isLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper
                          }
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Response (JSON) <span style={{ color: theme.palette.error.main }}>*</span>
                          </Typography>
                          {/* JSON validation indicator */}
                          {payload.response && (
                            <Tooltip title={payload.isValidJson ? 'Valid JSON' : 'Invalid JSON'}>
                              {payload.isValidJson ? (
                                <CheckCircleOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.success.main }} 
                                />
                              ) : (
                                <ErrorOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.error.main }} 
                                />
                              )}
                            </Tooltip>
                          )}
                        </Box>
                        {/* Format JSON and Edit in full screen buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Format JSON">
                            <IconButton
                              size="small"
                              onClick={() => handleFormatJson(payload.id)}
                              disabled={!payload.isValidJson || isLoading}
                              sx={{ 
                                color: payload.isValidJson ? theme.palette.primary.main : theme.palette.text.disabled,
                                p: 0.5
                              }}
                            >
                              <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add in full screen">
                            <IconButton
                              size="small"
                              onClick={() => setFullScreenPayloadId(payload.id)}
                              disabled={isLoading}
                              sx={{ 
                                color: theme.palette.primary.main,
                                p: 0.5
                              }}
                            >
                              <OpenInFullIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <TextField
                        value={payload.response}
                        onChange={(e) => handleUpdatePayload(payload.id, 'response', e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        disabled={isLoading}
                        placeholder='{"key": "value"}'
                        error={payload.response !== '' && !payload.isValidJson}
                        helperText={payload.response !== '' && !payload.isValidJson ? 'Invalid JSON format' : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            '& textarea': {
                              resize: 'vertical'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPayload}
              disabled={isLoading}
              sx={{ 
                mt: 1,
                textTransform: 'none'
              }}
            >
              Add Payload
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || payloads.some(p => !p.isValidJson)}
          sx={{ 
            textTransform: 'none',
            minWidth: 120
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Save Scenario'
          )}
        </Button>
      </DialogActions>
    </Dialog>

    {fullScreenPayloadId && (() => {
      const payload = payloads.find(p => p.id === fullScreenPayloadId);
      const payloadIndex = payloads.findIndex(p => p.id === fullScreenPayloadId);
      return (
        <FullScreenJsonEditorModal
          open={!!payload}
          onClose={() => setFullScreenPayloadId(null)}
          title={payload ? `Edit Response JSON – Payload ${payloadIndex + 1}` : ''}
          initialValue={payload?.response ?? '{}'}
          onSave={(value) => {
            if (fullScreenPayloadId) {
              handleUpdatePayload(fullScreenPayloadId, 'response', value);
              setFullScreenPayloadId(null);
            }
          }}
        />
      );
    })()}
  </>
  );
};
